/**
 * Stale-bundle recovery.
 *
 * A client deploy rolls the pods and every previous build's hashed chunk is
 * gone. A tab that loaded the old bundle keeps working until it hits a dynamic
 * `import()` for a chunk that no longer exists: the request 404s and whatever
 * needed that code dies quietly (a blank lazy route; on 2026-09-14 the carousel
 * Download toasted "try again" seven times in a row because the helper
 * lazy-imported the API client).
 *
 * One policy, two entry points:
 *   - `installStaleBundleGuard` listens for Vite's `vite:preloadError`, which
 *     the build's preload helper dispatches for EVERY failed dynamic import
 *     (the chunk itself or one of its preloaded deps). No call site has to
 *     remember to handle it.
 *   - `recoverFromStaleBundle` is the policy: reload the tab once so it picks
 *     up the current bundle. A second failure inside the window means the
 *     problem is not a stale bundle (offline, a real 5xx) and is surfaced
 *     instead of looping.
 *
 * The lazy routes in routes-getter keep an explicit wrapper on top so Suspense
 * stays pending while the reload lands instead of flashing the oops dialog.
 *
 * Rule of thumb this module exists to enforce: `import()` is for real code
 * splitting (routes, heavy optional libs). Never use it to dodge a test-runner
 * alias gap or a circular import; that manufactures a chunk that only exists
 * at click time.
 */

import { errorReportingService } from 'services/error-reporting/error-reporting.service'

const CHUNK_RELOAD_KEY = 'taka:chunk-reload-ts'
const CHUNK_RELOAD_WINDOW_MS = 10_000

const CHUNK_LOAD_ERROR_RE =
  /Failed to fetch dynamically imported module|Loading chunk|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i

export const isChunkLoadError = (err: unknown): boolean => {
  const msg = err instanceof Error ? err.message : String(err ?? '')
  return CHUNK_LOAD_ERROR_RE.test(msg)
}

/**
 * 'reloading': a reload is on its way, keep the caller pending.
 * 'surfaced':  no reload (offline, or one already fired inside the window);
 *              the caller should let the error through.
 */
export type StaleBundleOutcome = 'reloading' | 'surfaced'

/** Pure policy: reload unless a reload already fired inside the loop window. */
export const shouldReloadForStaleBundle = (lastReloadAt: number, now: number): boolean =>
  now - lastReloadAt >= CHUNK_RELOAD_WINDOW_MS

interface RecoveryDeps {
  now: () => number
  isOnline: () => boolean
  readLastReload: () => number
  writeLastReload: (ts: number) => void
  report: (err: Error, context: Record<string, unknown>) => void
  reload: () => void
  pathname: () => string
}

const toError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)))

/**
 * Builds the recovery function with injectable side effects (tests drive it
 * with fakes; the browser gets the defaults below). Outcomes are memoised per
 * error object so the preload listener and a route wrapper that both see the
 * same failure report it once, not twice.
 */
export function createStaleBundleRecovery(deps: RecoveryDeps) {
  let reloadScheduled = false
  const outcomes = new WeakMap<object, StaleBundleOutcome>()

  return function recoverFromStaleBundle(
    err: unknown,
    context: Record<string, unknown> = {}
  ): StaleBundleOutcome {
    if (typeof err === 'object' && err !== null) {
      const seen = outcomes.get(err)
      if (seen) return seen
    }
    const remember = (outcome: StaleBundleOutcome) => {
      if (typeof err === 'object' && err !== null) outcomes.set(err, outcome)
      return outcome
    }

    if (reloadScheduled) return remember('reloading')

    const now = deps.now()
    const scope = { ...context, pathname: deps.pathname() }
    if (!deps.isOnline()) {
      // The chunk is not missing, the network is. A reload would only throw
      // away the tab's state for the browser's offline page.
      deps.report(toError(err), { reason: 'chunk-load-offline', ...scope })
      return remember('surfaced')
    }
    if (!shouldReloadForStaleBundle(deps.readLastReload(), now)) {
      // Already reloaded once in this window. The second failure is something
      // other than a stale bundle. Surface it instead of looping.
      deps.report(toError(err), { reason: 'chunk-reload-loop', ...scope })
      return remember('surfaced')
    }

    reloadScheduled = true
    deps.writeLastReload(now)
    deps.report(toError(err), { reason: 'chunk-load-stale-bundle', ...scope })
    deps.reload()
    return remember('reloading')
  }
}

const readLastReload = (): number => {
  try {
    return Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)
  } catch {
    return 0
  }
}

const writeLastReload = (ts: number): void => {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(ts))
  } catch {
    // Storage can throw (private mode, blocked site data). A missing marker
    // only weakens the loop guard; the reload itself still goes ahead.
  }
}

export const recoverFromStaleBundle = createStaleBundleRecovery({
  now: () => Date.now(),
  // `onLine === false` is the only reliable signal; `true` can still be wrong,
  // which the loop guard absorbs.
  isOnline: () => typeof navigator === 'undefined' || navigator.onLine !== false,
  readLastReload,
  writeLastReload,
  report: (err, context) => errorReportingService.captureException(err, context),
  // The report survives the navigation: posthog-js and the Logs shipper both
  // drain their queues on pagehide (sendBeacon).
  reload: () => window.location.reload(),
  pathname: () => window.location.pathname,
})

/**
 * Window-level safety net for every dynamic import in the build. Installed
 * once from main.tsx, before React mounts, so it also covers the first lazy
 * route of the page.
 *
 * Deliberately does NOT `preventDefault()`: the import still rejects, so each
 * caller keeps its own error path (the route wrapper stays pending; a util's
 * toast is harmless for the moment until the reload lands). Preventing it
 * would resolve the import to `undefined` and crash the caller less obviously.
 */
export function installStaleBundleGuard(): () => void {
  if (typeof window === 'undefined') return () => {}
  const onPreloadError = (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload
    recoverFromStaleBundle(payload, { source: 'vite:preloadError' })
  }
  window.addEventListener('vite:preloadError', onPreloadError)
  return () => window.removeEventListener('vite:preloadError', onPreloadError)
}
